<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-alert v-if="errorMessage" dense outlined type="warning" class="mb-3">
            {{ errorMessage }}
         </v-alert>

         <v-card outlined class="mb-3">
            <v-card-text class="py-3">
               <v-row dense align="center">
                  <v-col cols="12" sm="7" md="8">
                     <div class="text-caption text-uppercase font-weight-bold grey--text">Statistics Filter</div>
                     <div class="subtitle-1 font-weight-bold">Dashboard data for {{ selectedYear }}</div>
                  </v-col>
                  <v-col cols="12" sm="5" md="4">
                     <v-select
                        v-model="selectedYear"
                        :items="yearOptions"
                        label="Filter by Year"
                        outlined
                        dense
                        hide-details
                        :loading="loading"
                        @change="loadStatistics"
                     ></v-select>
                  </v-col>
               </v-row>
            </v-card-text>
         </v-card>

         <!-- Summary Cards -->
         <v-row dense class="mb-2">
            <v-col cols="12" sm="6" lg="3" v-for="card in summaryCards" :key="card.label">
               <v-card outlined>
                  <v-card-text class="d-flex align-center justify-space-between py-4">
                     <div>
                        <div class="text-caption text-uppercase font-weight-bold grey--text">{{ card.label }}</div>
                        <div class="text-h5 font-weight-bold mt-1" :class="card.color + '--text'">{{ card.value }}</div>
                     </div>
                     <v-avatar :color="card.color" size="48">
                        <v-icon dark>{{ card.icon }}</v-icon>
                     </v-avatar>
                  </v-card-text>
               </v-card>
            </v-col>
         </v-row>

         <!-- Charts Row -->
         <v-row dense class="mb-2">
            <!-- Bar Chart -->
            <v-col cols="12" md="7">
               <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold">Payment Collection by Month ({{ selectedYear }})</v-card-title>
                  <v-card-subtitle class="pb-0">Breakdown by member type</v-card-subtitle>
                  <v-card-text>
                     <canvas ref="barChart" height="120"></canvas>
                  </v-card-text>
               </v-card>
            </v-col>

            <!-- Pie Chart -->
            <v-col cols="12" md="5">
               <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold">Overdue by Member Type</v-card-title>
                  <v-card-subtitle class="pb-0">Outstanding fees distribution</v-card-subtitle>
                  <v-card-subtitle class="pt-1 font-weight-bold primary--text">
                     Total: {{ overduePieTotal }}
                  </v-card-subtitle>
                  <v-card-text class="d-flex justify-center">
                     <canvas ref="pieChart" height="270" width="180"></canvas>
                  </v-card-text>
               </v-card>
            </v-col>
         </v-row>
         <!-- Membership Distribution Row -->
         <v-row dense class="mb-2">

            <!-- Region Chart -->
            <v-col cols="12" md="6">
               <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold">
                     Membership by Region
                  </v-card-title>

                  <v-card-subtitle class="pb-0">
                     Distribution across regions
                  </v-card-subtitle>

                  <v-card-text class="d-flex justify-center">
                     <canvas ref="regionChart" height="300"></canvas>
                  </v-card-text>
               </v-card>
            </v-col>

            <!-- Category Chart -->
            <v-col cols="12" sm="6" md="3">
               <v-card outlined class="fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                     Membership by Category
                  </v-card-title>

                  <v-card-subtitle class="pb-0">
                     Breakdown by member type
                  </v-card-subtitle>
                  <v-card-subtitle class="pt-1 font-weight-bold primary--text">
                     Total: {{ categoryPieTotal }}
                  </v-card-subtitle>

                  <v-card-text class="d-flex justify-center">
                     <canvas ref="categoryChart" height="260"></canvas>
                   </v-card-text>
                </v-card>
             </v-col>

            <v-col cols="12" sm="6" md="3">
               <v-card outlined class="fill-height">
                  <v-card-title class="subtitle-1 font-weight-bold">
                     Members Below 40
                  </v-card-title>
                  <v-card-subtitle class="pb-0">
                     Active Student, Graduate and Corporate members
                  </v-card-subtitle>
                  <v-card-subtitle class="pt-1 font-weight-bold primary--text">
                     Total: {{ below40PieTotal }}
                  </v-card-subtitle>

                  <v-card-text class="d-flex justify-center">
                     <canvas ref="below40Chart" height="260"></canvas>
                  </v-card-text>
               </v-card>
            </v-col>

         </v-row>

      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import Chart from 'chart.js'

const axios = require('axios')

export default {
   name: "MOMStatistics",
   props: {
      user: Object
   },
   components: {
      DashboardLayout,
      BaseBreadcrumb,
   },

   data: () => ({
      page: {
         title: "Statistics",
         icon: "mdi-chart-bar",
      },
      breadcrumbs: [
         { text: "Dashboard", disabled: false, href: "/dashboard" },
         { text: "Statistics", disabled: true },
      ],
      search: '',
      loading: false,
      errorMessage: '',
      barChartInstance: null,
      pieChartInstance: null,
       regionChartInstance: null,
       categoryChartInstance: null,
       below40ChartInstance: null,

      // Summary card data — replace with API values
      summaryCards: [
         { label: "Total Overdue Fees",      value: "RM 0.00",  icon: "mdi-alert-circle-outline", color: "error"   },
         { label: "Total Collected",         value: "RM 0.00",  icon: "mdi-cash-check",           color: "success" },
         { label: "Pending Payments",        value: "RM 0.00",  icon: "mdi-clock-outline",        color: "warning" },
         { label: "Members with Overdue",    value: "0",        icon: "mdi-account-alert",        color: "info"    },
      ],

      // Bar chart data — replace with API values
      barData: {
         labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
         datasets: [
            {
               label: 'Ordinary',
               data: [0, 0, 0, 0, 0, 0],
               backgroundColor: '#1976D2',
            },
            {
               label: 'Associate',
               data: [0, 0, 0, 0, 0, 0],
               backgroundColor: '#43A047',
            },
            {
               label: 'Graduate',
               data: [0, 0, 0, 0, 0, 0],
               backgroundColor: '#FB8C00',
            },
         ]
      },

      // Pie chart data — replace with API values
      pieData: {
         labels: ['Ordinary', 'Associate', 'Graduate'],
         datasets: [{
            data: [0, 0, 0],
            backgroundColor: ['#1976D2', '#43A047', '#FB8C00'],
         }]
      },

      regionData: {
         labels: [],
         datasets: [{
            data: [],
            backgroundColor: []
         }]
      },

       categoryData: {
         labels: [],
         datasets: [{
            data: [],
            backgroundColor: []
         }]
       },
       below40Data: {
          labels: ['Student', 'Graduate', 'Corporate'],
          datasets: [{
             data: [0, 0, 0],
             backgroundColor: ['#8E24AA', '#43A047', '#1976D2']
          }]
       },
      selectedYear: new Date().getFullYear(),
      yearOptions: [new Date().getFullYear()],
   }),

   computed: {
      overduePieTotal() {
         return this.sumChartValues(this.pieData.datasets[0].data).toLocaleString('en-MY')
      },

       categoryPieTotal() {
          return this.sumChartValues(this.categoryData.datasets[0].data).toLocaleString('en-MY')
       },

       below40PieTotal() {
          return this.sumChartValues(this.below40Data.datasets[0].data).toLocaleString('en-MY')
       },
   },

   methods: {
      statusColor(status) {
         const map = {
            'Active':   'success',
            'Overdue':  'error',
            'Pending':  'warning',
            'Disabled': 'grey',
         }
         return map[status] || 'grey'
      },

      formatCurrency(value) {
         return 'RM ' + Number(value || 0).toLocaleString('en-MY', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
         })
      },

      sumChartValues(values) {
         return (values || []).reduce((total, value) => total + Number(value || 0), 0)
      },

      initBarChart() {
         if (!this.$refs.barChart || !Chart) {
            return
         }

         const ctx = this.$refs.barChart.getContext('2d')
         this.barChartInstance = new Chart(ctx, {
            type: 'bar',
            data: this.barData,
            options: {
               responsive: true,
               maintainAspectRatio: true,
               legend: { position: 'bottom' },
               scales: {
                  xAxes: [{ stacked: false }],
                  yAxes: [{ stacked: false, ticks: { beginAtZero: true } }],
               }
            }
         })
      },

      initPieChart() {
         if (!this.$refs.pieChart || !Chart) {
            return
         }

         const ctx = this.$refs.pieChart.getContext('2d')
         this.pieChartInstance = new Chart(ctx, {
            type: 'doughnut',
            data: this.pieData,
            options: {
               responsive: false,
               legend: { position: 'bottom' },
            }
         })
      },

      initRegionChart() {
         const ctx = this.$refs.regionChart.getContext('2d')

         this.regionChartInstance = new Chart(ctx, {
            type: 'horizontalBar', // 👈 Chart.js v2 style
            data: this.regionData,
            options: {
               responsive: true,
               maintainAspectRatio: true,
               legend: { display: false },
               scales: {
                  xAxes: [{
                     ticks: {
                        beginAtZero: true
                     }
                  }],
                  yAxes: [{
                     barPercentage: 0.6,
                     categoryPercentage: 0.7
                  }]
               }
            }
         })
      },

       initCategoryChart() {
         const ctx = this.$refs.categoryChart.getContext('2d')

         this.categoryChartInstance = new Chart(ctx, {
            type: 'pie',
            data: this.categoryData,
            options: {
               responsive: true,
               legend: { position: 'bottom' }
            }
         })
       },

       initBelow40Chart() {
          const ctx = this.$refs.below40Chart.getContext('2d')

          this.below40ChartInstance = new Chart(ctx, {
             type: 'pie',
             data: this.below40Data,
             options: {
                responsive: true,
                legend: { position: 'bottom' }
             }
          })
       },

      loadStatistics() {
         const currentYear = new Date().getFullYear()
         if (Number(this.selectedYear) > currentYear) {
            this.selectedYear = currentYear
         }

         this.loading = true
         axios.get('/admin/statistics', {
            params: {
               year: this.selectedYear
            }
         })
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  const d = resp.data.data
                  const barLabels = d.bar_labels || this.barData.labels
                  const barDatasets = d.bar_datasets && d.bar_datasets.length ? d.bar_datasets : this.barData.datasets
                  const pieLabels = d.pie_labels && d.pie_labels.length ? d.pie_labels : this.pieData.labels
                  const pieData = d.pie_data && d.pie_data.length ? d.pie_data : this.pieData.datasets[0].data
                  const pieColors = d.pie_colors && d.pie_colors.length ? d.pie_colors : this.pieData.datasets[0].backgroundColor

                  this.summaryCards[0].value = this.formatCurrency(d.total_overdue)
                  this.summaryCards[1].value = this.formatCurrency(d.total_collected)
                  this.summaryCards[2].value = this.formatCurrency(d.total_pending)
                  this.summaryCards[3].value = String(d.members_with_overdue || 0)
                  this.selectedYear = d.selected_year || this.selectedYear
                  this.yearOptions = (d.year_options && d.year_options.length ? d.year_options : this.yearOptions)
                     .map(year => Number(year))
                     .filter(year => year >= 2000 && year <= currentYear)
                  if (!this.yearOptions.includes(currentYear)) {
                     this.yearOptions.push(currentYear)
                  }
                  this.yearOptions.sort((a, b) => b - a)

                  // BAR
                  this.barData.labels = barLabels
                  this.barData.datasets = barDatasets
                  if (this.barChartInstance) {
                     this.barChartInstance.data.labels = barLabels
                     this.barChartInstance.data.datasets = barDatasets
                     this.barChartInstance.update()
                  }

                  // PIE
                  this.pieData.labels = pieLabels
                  this.pieData.datasets[0].data = pieData
                  this.pieData.datasets[0].backgroundColor = pieColors
                  if (this.pieChartInstance) {
                     this.pieChartInstance.data.labels = pieLabels
                     this.pieChartInstance.data.datasets[0].data = pieData
                     this.pieChartInstance.data.datasets[0].backgroundColor = pieColors
                     this.pieChartInstance.update()
                  }

                  // REGION
                  this.regionData.labels = d.region_labels
                  this.regionData.datasets[0].data = d.region_data
                  this.regionData.datasets[0].backgroundColor = d.region_colors
                  if (this.regionChartInstance) this.regionChartInstance.update()

                  // CATEGORY
                  this.categoryData.labels = d.category_labels
                  this.categoryData.datasets[0].data = d.category_data
                  this.categoryData.datasets[0].backgroundColor = d.category_colors
                  if (this.categoryChartInstance) this.categoryChartInstance.update()

                  // MEMBERS BELOW 40
                  this.below40Data.labels = d.below_40_labels || this.below40Data.labels
                  this.below40Data.datasets[0].data = d.below_40_data || [0, 0, 0]
                  this.below40Data.datasets[0].backgroundColor = d.below_40_colors || this.below40Data.datasets[0].backgroundColor
                  if (this.below40ChartInstance) this.below40ChartInstance.update()
               } else {
                  this.errorMessage = (resp.data && resp.data.error) ? resp.data.error : 'Unable to load statistics data.'
               }
            })
            .catch((error) => {
               this.errorMessage = error.message || 'Unable to load statistics data.'
            })
            .finally(() => {
               this.loading = false
            })
      },
   },

   mounted() {
      this.$nextTick(() => {
         try {
            this.initBarChart()
            this.initPieChart()
            this.initRegionChart()
            this.initCategoryChart()
            this.initBelow40Chart()
         } catch (error) {
            this.errorMessage = error.message || 'Unable to initialize charts.'
         }
         this.loadStatistics()
      })
   },

   beforeDestroy() {
      if (this.barChartInstance) this.barChartInstance.destroy()
      if (this.pieChartInstance) this.pieChartInstance.destroy()
      if (this.regionChartInstance) this.regionChartInstance.destroy()
      if (this.categoryChartInstance) this.categoryChartInstance.destroy()
      if (this.below40ChartInstance) this.below40ChartInstance.destroy()
   },
}
</script>

<style scoped>
</style>
